import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Lightbulb, RefreshCw, Link2, Copy, CheckCircle, ArrowUpRight, NotebookPen, Send, Loader2, AlertCircle, Network, ArrowRight } from "lucide-react";
import { useOrgId } from "../hooks/useOrgId";
import * as api from "../lib/api";
import { useCmsConnected } from "../components/CmsGate";
import { openSearchTopic } from "../components/search-performance/TopicParts";
import { usePersistentState } from "../components/search-performance/shared";

const PRIMARY = "#018D4C";
const PRIMARY_LIGHT = "#E6F5EE";

const PRIORITY_STYLES = {
  high: { bg: "#FF2A0015", text: "#FF2A00", border: "#FF2A00" },
  medium: { bg: "#FF8C0015", text: "#FF8C00", border: "#FF8C00" },
  low: { bg: "#018D4C15", text: PRIMARY, border: PRIMARY },
};

// Search topics whose next move is writing: a new page, or a rewrite of one ranking past
// page 1. Jev's next_move decides; before it has run, coverage stands in (gap → write
// new, weak → expand). Already sorted lead tier → priority by the backend.
const WRITE_MOVES = {
  write_new:   "Write a new pillar page",
  expand_page: "Expand the existing page",
};
const TIER_PRIORITY = { high: "HIGH", medium: "MEDIUM" };

function topicsToWrite(topics) {
  return topics
    .map(t => {
      const move = t.next_move || (t.coverage_status === "gap" ? "write_new" : t.coverage_status === "weak" ? "expand_page" : null);
      if (!WRITE_MOVES[move]) return null;
      const demand = `${(t.impressions || 0).toLocaleString()} impressions across ${t.queries} searches`;
      return {
        title: t.topic,
        cluster: "Search topic",
        priority: TIER_PRIORITY[t.lead_tier] || "LOW",
        reason: [t.problem, `${WRITE_MOVES[move]}: ${demand}, coverage ${t.coverage_status}.`].filter(Boolean).join(" "),
        topic_id: t.id,
      };
    })
    .filter(Boolean);
}

async function pollOutlineReady(id, onStatus) {
  const MAX_ACTIVE_POLLS = 60; // 2 min at 2s intervals, only counted while actually running
  const MAX_TOTAL_POLLS = 450; // 15 min hard ceiling regardless of queue depth
  let activePolls = 0;
  for (let i = 0; i < MAX_TOTAL_POLLS; i++) {
    await new Promise(r => setTimeout(r, 2000));
    const { status, error, review, queue_position } = await api.getOutlineStatus(id);
    if (status === "done") return review || null;
    if (status === "error") throw new Error(error || "Outline generation failed");
    onStatus?.({ status, queue_position });
    if (status === "queued") continue; // legitimately waiting behind other jobs — doesn't burn the active-poll budget
    if (++activePolls >= MAX_ACTIVE_POLLS) throw new Error("Outline generation timed out after 2 minutes");
  }
  throw new Error("Outline generation timed out");
}

// approve states: idle | queued | generating | pushing | done | outlined | error | needs_revision
// Without a CMS (cms === false) approve stops at "outlined": the outline opens in the
// editor, where it can be copied or downloaded as markdown.
function RecommendationCard({ item, index, pushedOutline, pendingOutlineId, cms, testPrefix = "recommendation", footerExtra = null }) {
  const [copied, setCopied] = useState(false);
  const [approveState, setApproveState] = useState(pushedOutline ? "done" : "idle");
  const [wpLink, setWpLink] = useState(pushedOutline?.wp_draft_link || pushedOutline?.wp_draft_edit_link || null);
  const [approveError, setApproveError] = useState(null);
  const [outlineId, setOutlineId] = useState(pushedOutline?.id || null);
  const [wpPostStatus, setWpPostStatus] = useState(null); // "draft" | "publish" | null
  const [checkingWpStatus, setCheckingWpStatus] = useState(false);
  const [reviewIssues, setReviewIssues] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [queuePosition, setQueuePosition] = useState(null);
  const navigate = useNavigate();
  const style = PRIORITY_STYLES[item.priority?.toLowerCase()] || PRIORITY_STYLES.medium;

  const copy = () => {
    navigator.clipboard.writeText(item.title);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const pushToWp = async (id) => {
    if (cms === false) {
      setOutlineId(id);
      setApproveState("outlined");
      return;
    }
    setApproveState("pushing");
    const result = await api.pushOutlineToWp(id);
    if (result.success) {
      setOutlineId(id);
      setWpLink(result.link || result.edit_link || null);
      setApproveState("done");
    } else {
      throw new Error(result.detail || "Push failed");
    }
  };

  const onQueueStatus = ({ status, queue_position }) => {
    if (status === "queued") {
      setApproveState("queued");
      setQueuePosition(queue_position);
    } else {
      setApproveState("generating");
      setQueuePosition(null);
    }
  };

  const finishAfterGenerate = async (id, review) => {
    if (review?.status === "needs_revision") {
      setOutlineId(id);
      setReviewIssues(review.issues || []);
      setApproveState("needs_revision");
      return;
    }
    await pushToWp(id);
  };

  const approve = async () => {
    setApproveState("generating");
    setApproveError(null);
    setReviewIssues(null);
    setQueuePosition(null);
    try {
      const outline = await api.generateOutline({
        title: item.title,
        cluster: item.cluster || null,
        reason: item.reason || null,
      });
      setOutlineId(outline.id);

      let review = null;
      if (outline.status === "generating") {
        review = await pollOutlineReady(outline.id, onQueueStatus);
      }

      await finishAfterGenerate(outline.id, review);
    } catch (e) {
      console.error("[approve] failed", e);
      setApproveError(e?.response?.data?.detail || e.message || "Failed");
      setApproveState("error");
    }
  };

  // A page reload loses in-memory React state — pushedMap (built from
  // wp_pushed_at) only resurrects cards that already finished pushing. A
  // card whose outline row exists but hasn't been pushed yet (still
  // queued/generating, needs_revision, or errored) would otherwise reset to
  // a bare "APPROVE" button, orphaning that in-flight/failed job and letting
  // a re-click spawn a duplicate outline row for the same title. Resume
  // tracking it instead.
  useEffect(() => {
    if (!pendingOutlineId || cms === null || approveState !== "idle") return;
    let cancelled = false;
    (async () => {
      try {
        const job = await api.getOutlineStatus(pendingOutlineId);
        if (cancelled) return;

        if (job.status === "needs_revision" || job.review?.status === "needs_revision") {
          setOutlineId(pendingOutlineId);
          setReviewIssues(job.review?.issues || []);
          setApproveState("needs_revision");
          return;
        }
        if (job.status === "queued" || job.status === "generating" || job.status === "reviewing") {
          setOutlineId(pendingOutlineId);
          onQueueStatus({ status: job.status, queue_position: job.queue_position });
          const review = await pollOutlineReady(pendingOutlineId, onQueueStatus);
          if (cancelled) return;
          await finishAfterGenerate(pendingOutlineId, review);
          return;
        }

        // job.status is either a genuine "error", or the registry's default
        // "done" for an id it has no record of — a server restart wipes the
        // in-memory job registry entirely, so every job that was still
        // mid-flight (queued/generating/erroring) at that moment falls back
        // to this same "done" default, whether or not it actually finished.
        // Check the outline row itself: a restart can kill a background task
        // before it ever writes blocks back, and "Push anyway" on an outline
        // with no blocks would ship an empty draft.
        const doc = await api.getOutline(pendingOutlineId);
        if (cancelled) return;
        const hasBlocks = Array.isArray(doc.blocks) && doc.blocks.length > 0;
        if (hasBlocks && cms === false) {
          setOutlineId(pendingOutlineId);
          setApproveState("outlined");
          return;
        }
        if (hasBlocks) {
          setOutlineId(pendingOutlineId);
          setApproveError(job.error || "Outline generated but not pushed — retry to push, or regenerate.");
        } else {
          setApproveError(job.error || "Generation was interrupted before it finished — click Retry to regenerate.");
        }
        setApproveState("error");
      } catch (e) {
        if (!cancelled) console.error("[resume] status check failed", e);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOutlineId, cms]);

  const pushAnyway = async () => {
    setApproveError(null);
    try {
      await pushToWp(outlineId);
    } catch (e) {
      console.error("[approve] push-anyway failed", e);
      setApproveError(e?.response?.data?.detail || e.message || "Failed");
      setApproveState("error");
    }
  };

  const publish = async () => {
    if (!outlineId || publishing) return;
    setPublishing(true);
    setApproveError(null);
    try {
      await api.updateOutlineWpStatus(outlineId, "publish");
      setWpPostStatus("publish");
    } catch (e) {
      console.error("[publish] failed", e);
      setApproveError(e?.response?.data?.detail || e.message || "Publish failed");
    } finally {
      setPublishing(false);
    }
  };

  const checkWpStatus = async () => {
    if (!outlineId || checkingWpStatus) return;
    setCheckingWpStatus(true);
    try {
      const res = await api.getOutlineWpStatus(outlineId);
      setWpPostStatus(res.status || null);
      if (res.link) setWpLink(res.link);
    } catch (e) {
      console.error("[wp-status] check failed", e);
    } finally {
      setCheckingWpStatus(false);
    }
  };

  // wpPostStatus always inits to null — a card resurrected as "done" (via
  // pushedOutline on page load) has no way to know from list_outlines alone
  // whether the draft was since published, so it defaults to showing "Draft
  // live" even for an already-published post until the refresh icon is
  // clicked. Check once on mount instead of requiring that manual click.
  useEffect(() => {
    if (pushedOutline?.id) checkWpStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderApprove = () => {
    if (approveState === "idle") return (
      <button
        onClick={approve}
        data-testid={`approve-${testPrefix}-${index}`}
        title={cms === false ? "Generate an outline you can edit, copy or download" : "Generate outline & push draft"}
        className="flex items-center gap-1 px-2 py-1 text-[10px] font-bold tracking-wide rounded-lg transition-colors hover:opacity-90 text-white"
        style={{ backgroundColor: PRIMARY }}
      >
        {cms === false ? <NotebookPen size={10} strokeWidth={2.5} /> : <Send size={10} strokeWidth={2.5} />}
        {cms === false ? "WRITE OUTLINE" : "APPROVE"}
      </button>
    );
    if (approveState === "outlined") return (
      <button
        onClick={() => navigate(`/dashboard/writing/${outlineId}`)}
        className="flex items-center gap-1 text-[10px] font-bold text-green-600 hover:underline underline-offset-2"
      >
        <CheckCircle size={11} strokeWidth={2.5} />
        Outline ready · Open →
      </button>
    );
    if (approveState === "queued") return (
      <span className="flex items-center gap-1 text-[10px] text-zinc-400 font-bold">
        <Loader2 size={11} className="animate-spin" />
        {queuePosition > 0 ? `Queued (${queuePosition} ahead)` : "Queued — next up…"}
      </span>
    );
    if (approveState === "generating") return (
      <span className="flex items-center gap-1 text-[10px] text-zinc-400 font-bold">
        <Loader2 size={11} className="animate-spin" />
        Outlining…
      </span>
    );
    if (approveState === "pushing") return (
      <span className="flex items-center gap-1 text-[10px] text-zinc-400 font-bold">
        <Loader2 size={11} className="animate-spin" />
        Pushing…
      </span>
    );
    if (approveState === "done") {
      const isPublished = wpPostStatus === "publish";
      const label = isPublished ? "Published" : "Draft live";
      const color = isPublished ? "text-emerald-700" : "text-green-600";
      return (
        <span className="flex items-center gap-1.5 text-[10px] font-bold">
          <CheckCircle size={11} strokeWidth={2.5} className={color} />
          {wpLink ? (
            <a href={wpLink} target="_blank" rel="noopener noreferrer" className={`underline underline-offset-2 ${color}`}>
              {label}
            </a>
          ) : <span className={color}>{label}</span>}
          {outlineId && !isPublished && (
            <button
              onClick={publish}
              disabled={publishing}
              title="Publish this draft to your CMS"
              className="text-[10px] font-bold underline underline-offset-2 text-zinc-400 hover:text-zinc-700 transition-colors disabled:opacity-50"
            >
              {publishing ? "Publishing…" : "Publish"}
            </button>
          )}
          {outlineId && (
            <button
              onClick={checkWpStatus}
              title="Check current draft status"
              className="text-zinc-400 hover:text-zinc-600 transition-colors"
            >
              {checkingWpStatus
                ? <Loader2 size={10} className="animate-spin" />
                : <RefreshCw size={10} strokeWidth={2} />}
            </button>
          )}
        </span>
      );
    }
    if (approveState === "needs_revision") return (
      <span className="flex items-center gap-2 text-[10px] font-bold">
        <span className="flex items-center gap-1" style={{ color: "#FF8C00" }}>
          <AlertCircle size={11} />
          Needs revision
        </span>
        <button onClick={pushAnyway} className="text-zinc-400 hover:text-zinc-700 underline underline-offset-2">
          {cms === false ? "Use anyway" : "Push anyway"}
        </button>
        <button onClick={approve} className="text-zinc-400 hover:text-zinc-700 underline underline-offset-2">
          Regenerate
        </button>
      </span>
    );
    if (approveState === "error") return (
      <span className="flex items-center gap-2 text-[10px] font-bold">
        <button
          onClick={approve}
          title={approveError}
          className="flex items-center gap-1 text-red-500 hover:text-red-700"
        >
          <AlertCircle size={11} />
          Retry
        </button>
        {outlineId && (
          <button onClick={pushAnyway} className="text-zinc-400 hover:text-zinc-700 underline underline-offset-2">
            Push anyway
          </button>
        )}
      </span>
    );
    return null;
  };

  return (
    <div
      data-testid={`${testPrefix}-card-${index}`}
      className="bg-white rounded-xl border border-zinc-100 p-5 hover:shadow-md transition-all shadow-sm"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span
              className="px-2 py-0.5 text-[9px] tracking-[0.2em] font-bold uppercase rounded-full"
              style={{ background: style.bg, color: style.text }}
            >
              {item.priority || "medium"}
            </span>
            {item.topic_id ? (
              <button onClick={() => openSearchTopic(navigate, item.topic_id)} className="text-[10px] text-zinc-400 hover:text-zinc-700 hover:underline">
                Search topic →
              </button>
            ) : item.cluster && (
              <span className="text-[10px] text-zinc-400">{item.cluster}</span>
            )}
          </div>
          <h3 className="text-sm font-bold text-zinc-900 leading-tight mb-1.5">
            {item.title}
          </h3>
          {item.reason && (
            <p className="text-xs text-zinc-500 leading-relaxed">{item.reason}</p>
          )}
        </div>
        <button
          onClick={copy}
          data-testid={`copy-${testPrefix}-${index}`}
          title="Copy title"
          className="flex-shrink-0 p-2 hover:bg-zinc-100 transition-colors mt-0.5 rounded-lg"
        >
          {copied ? <CheckCircle size={14} className="text-green-600" strokeWidth={2} /> : <Copy size={14} className="text-zinc-400" />}
        </button>
      </div>
      <div className="mt-3 pt-3 border-t border-zinc-50 flex items-center justify-between">
        {renderApprove()}
        {approveState === "error" && approveError && (
          <span className="text-[9px] text-red-400 truncate max-w-[140px]" title={approveError}>{approveError}</span>
        )}
        {footerExtra}
      </div>
      {approveState === "needs_revision" && reviewIssues?.length > 0 && (
        <ul className="mt-2 pt-2 border-t border-zinc-50 space-y-1">
          {reviewIssues.slice(0, 3).map((issue, i) => (
            <li key={i} className="text-[9px] text-zinc-500 leading-snug">
              <span className="font-bold uppercase tracking-wide" style={{ color: "#FF8C00" }}>{issue.severity || "issue"}</span>{" "}
              {issue.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Topic cluster plan ───────────────────────────────────────────────────────
// One search topic → four-tier plan (homepage → pillar → cluster articles → sub-articles)
// built from the topic's own GSC queries, plus the internal links between the pages.
// See backend/services/topic_plan_service.py.

const TIER_LABELS = {
  2: "Tier 2 · Pillar page",
  3: "Tier 3 · Cluster articles",
  4: "Tier 4 · Sub-articles",
};

const pathOf = (url) => { try { return new URL(url).pathname; } catch { return url; } };

function planItem(node, topic, priority) {
  const searches = node.queries.slice(0, 4).map(q => q.query).join(", ");
  const demand = `${node.impressions.toLocaleString()} impressions across ${node.query_count} searches`;
  const sections = node.sections?.length ? `Sections, each linking to its article: ${node.sections.map(x => x.title).join("; ")}.` : null;
  const faq = node.faq?.length ? `FAQ: ${node.faq.join("; ")}.` : null;
  return {
    title: node.title,
    cluster: topic,
    priority,
    reason: [
      node.existing_url && `Update the existing page ${pathOf(node.existing_url)} rather than writing a new one.`,
      node.angle, `Targets: ${searches} (${demand}).`, sections, faq,
    ].filter(Boolean).join(" "),
  };
}

function ExistingPage({ url }) {
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" title="These searches already land on this page"
       className="ml-auto flex items-center gap-1 text-[10px] font-bold text-amber-700 hover:underline">
      Update {pathOf(url)} <ArrowUpRight size={10} />
    </a>
  );
}

function TopicPlan({ idea, cms, pushedMap, pendingMap }) {
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);
  // "loading" = reading a stored plan (no AI); "generating" = first plan or Regenerate (uses credits).
  const [busy, setBusy] = useState("loading");

  const load = async (regenerate = false) => {
    setError(null);
    try {
      if (!regenerate) {
        setBusy("loading");
        const stored = await api.getTopicPlan(idea.topic_id, { peek: true });
        if (!stored.missing) return setPlan(stored);
      }
      setBusy("generating");
      setPlan(await api.getTopicPlan(idea.topic_id, { regenerate }));
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(null);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [idea.topic_id]);

  if (busy && !plan) return (
    <p className="flex items-center gap-2 text-xs text-zinc-400 py-3">
      <Loader2 size={12} className="animate-spin" />
      {busy === "generating"
        ? "Grouping this topic's searches into a pillar and cluster articles… one-time, uses credits; saved afterwards."
        : "Loading saved plan…"}
    </p>
  );
  if (error && !plan) return (
    <p className="text-xs text-red-500 py-3">
      {error} <button onClick={() => load()} className="underline">Retry</button>
    </p>
  );

  const titles = { home: "Homepage", pillar: plan.pillar.title, key_page: `Service page ${pathOf(plan.key_page || "")}` };
  plan.articles.forEach(a => {
    titles[a.id] = a.title;
    a.children.forEach(c => { titles[c.id] = c.title; });
  });
  const card = (node, i) => {
    const item = planItem(node, plan.topic, idea.priority);
    const key = item.title.trim().toLowerCase();
    return (
      <RecommendationCard
        key={node.id}
        item={item}
        index={i}
        testPrefix={`plan-${node.id}`}
        pushedOutline={pushedMap[key]}
        pendingOutlineId={pendingMap[key]}
        cms={cms}
        footerExtra={<ExistingPage url={node.existing_url} />}
      />
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] text-zinc-500">
          <span className="font-bold text-zinc-700">Hybrid hub:</span> your service page stays the navigation layer; the pillar and its articles link to it, to each other and to related topics. <span className="font-bold text-zinc-700">Tier 1 · Homepage</span> links to the pillar.
        </p>
        <span className="flex items-center gap-2 text-[10px] text-zinc-400">
          {plan.stale && <span className="text-amber-600 font-bold">Searches changed since this plan.</span>}
          {plan.generated_at && <>Written {new Date(plan.generated_at).toLocaleString()}</>}
          <button onClick={() => load(true)} disabled={!!busy} className="font-bold hover:underline disabled:opacity-50" style={{ color: PRIMARY }}>
            {busy ? "Regenerating…" : "Regenerate"}
          </button>
        </span>
      </div>

      <div>
        <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-zinc-400 mb-2">{TIER_LABELS[2]}</p>
        {card(plan.pillar, 0)}
        {(plan.pillar.sections?.length > 0 || plan.pillar.faq?.length > 0) && (
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3 text-[11px]">
            {plan.pillar.sections?.length > 0 && (
              <div>
                <p className="font-bold text-zinc-500 mb-1">Pillar sections (each ↔ its article)</p>
                <ol className="list-decimal ml-4 text-zinc-700 space-y-0.5">
                  {plan.pillar.sections.map(x => <li key={x.article_id}>{x.title}</li>)}
                </ol>
              </div>
            )}
            {plan.pillar.faq?.length > 0 && (
              <div>
                <p className="font-bold text-zinc-500 mb-1">Pillar FAQ (from real searches)</p>
                <ul className="list-disc ml-4 text-zinc-700 space-y-0.5">
                  {plan.pillar.faq.map(q => <li key={q}>{q}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {plan.articles.length > 0 ? (
        <div>
          <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-zinc-400 mb-2">{TIER_LABELS[3]} ({plan.articles.length})</p>
          <div className="space-y-3">
            {plan.articles.map((a, i) => (
              <div key={a.id}>
                {card(a, i)}
                {a.children.length > 0 && (
                  <div className="mt-2 ml-6 pl-3 border-l-2 border-zinc-100 space-y-2">
                    <p className="text-[9px] tracking-[0.15em] uppercase font-bold text-zinc-400">{TIER_LABELS[4]}</p>
                    {a.children.map((c, j) => card(c, j))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-xs text-zinc-400">
          All of this topic's searches mean the same thing, so one pillar page covers them. No cluster articles needed yet.
        </p>
      )}

      {plan.extra.length > 0 && (
        <div>
          <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-zinc-400 mb-2">Extra ideas · no search data yet</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {plan.extra.map((e, i) => (
              <RecommendationCard
                key={e.title}
                item={{ title: e.title, cluster: plan.topic, priority: "LOW", reason: e.angle }}
                index={i}
                testPrefix="plan-extra"
                pushedOutline={pushedMap[e.title.trim().toLowerCase()]}
                pendingOutlineId={pendingMap[e.title.trim().toLowerCase()]}
                cms={cms}
              />
            ))}
          </div>
        </div>
      )}

      {plan.related_pillars?.length > 0 && (
        <div>
          <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-zinc-400 mb-2">Link the pillar to related topics</p>
          <div className="flex flex-wrap gap-2">
            {plan.related_pillars.map(r => (
              <button key={r.topic_id} onClick={() => openSearchTopic(navigate, r.topic_id)}
                      className="px-2.5 py-1 text-[11px] font-bold rounded-full border border-zinc-200 bg-white hover:border-zinc-400 text-zinc-700">
                {r.label} <span className="text-zinc-400 font-normal">{Math.round(r.similarity * 100)}%</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div>
        <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-zinc-400 mb-2">Internal links to add ({plan.links.length})</p>
        <ul className="bg-white border border-zinc-100 rounded-lg divide-y divide-zinc-50">
          {plan.links.map((l, i) => (
            <li key={i} className="flex items-center gap-2 px-3 py-1.5 text-[11px]">
              <span className="text-zinc-800 truncate">{titles[l.source]}</span>
              <ArrowRight size={10} className="text-zinc-300 shrink-0" />
              <span className="text-zinc-800 truncate">{titles[l.target]}</span>
              <span className="ml-auto text-[10px] text-zinc-400 whitespace-nowrap">{l.why}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function LinkingCard({ suggestion }) {
  const { article, link_to } = suggestion;

  return (
    <div data-testid="linking-suggestion-card" className="bg-white rounded-xl border border-zinc-100 shadow-sm">
      <div className="px-5 py-4 border-b border-zinc-100">
        <div className="flex items-center justify-between">
          <p className="text-sm font-bold text-zinc-900">
            {article.title}
          </p>
          {article.wp_url && (
            <a
              href={article.wp_url}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-2 flex-shrink-0 text-zinc-400 hover:text-zinc-900"
            >
              <ArrowUpRight size={13} />
            </a>
          )}
        </div>
        {article.cluster_id && (
          <span className="text-[10px] text-zinc-400 tracking-widest uppercase mt-0.5 inline-block">
            {article.cluster_id}
          </span>
        )}
      </div>
      <div className="p-4 space-y-2">
        <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-zinc-500 mb-3">
          Link to these articles:
        </p>
        {link_to.map((target, i) => {
          const pct = Math.round(target.similarity_score * 100);
          return (
            <div key={i} className="flex items-center justify-between gap-3 px-3 py-2 bg-zinc-50 border border-zinc-100 rounded-lg">
              <span className="text-xs text-zinc-900 flex-1 line-clamp-1">{target.title}</span>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-[10px] font-bold" style={{ color: PRIMARY }}>{pct}% match</span>
                {target.wp_url && (
                  <a href={target.wp_url} target="_blank" rel="noopener noreferrer" className="text-zinc-400 hover:text-zinc-900">
                    <ArrowUpRight size={11} />
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Recommendations() {
  const navigate = useNavigate();
  const [recData, setRecData] = useState({ suggestions: [], generated_at: null, status: "not_generated" });
  const [linkData, setLinkData] = useState({ suggestions: [] });
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(true);

  const [pushedMap, setPushedMap] = useState({});
  const [pendingMap, setPendingMap] = useState({});
  const [topicIdeas, setTopicIdeas] = useState(null);
  // Remembered across reloads; a topic id from an older Gap Analysis run just matches no card.
  const [openPlan, setOpenPlan] = usePersistentState("recs.openPlan", null);

  const orgId = useOrgId();
  const cms = useCmsConnected();
  // No click needed: topic coverage already says what to write.
  useEffect(() => {
    api.getQueryTopics({ limit: 100 })
      .then(d => setTopicIdeas(topicsToWrite(d.topics || [])))
      .catch(() => setTopicIdeas([]));
  }, [orgId]);
  useEffect(() => {
    Promise.all([
      api.getRecommendations(),
      api.getLinkingSuggestions(),
      api.listOutlines(),
    ])
      .then(([recs, links, outlineRes]) => {
        setRecData(recs);
        setLinkData(links);
        // outlineRes.outlines is ordered newest-first (updated_at desc) — keep
        // only the first (most recent) row per title in each map.
        const pushed = {};
        const pending = {};
        for (const o of outlineRes.outlines || []) {
          const key = o.title.trim().toLowerCase();
          if (o.wp_pushed_at) {
            if (!pushed[key]) pushed[key] = o;
          } else if (!pending[key]) {
            pending[key] = o.id;
          }
        }
        setPushedMap(pushed);
        setPendingMap(pending);

        const suggestions = recs.suggestions || [];
        const pushedCount = suggestions.filter(s => pushed[s.title?.trim().toLowerCase()]).length;
        if (suggestions.length > 0 && pushedCount / suggestions.length >= 0.3) {
          setGenerating(true);
          api.generateRecommendations({ top_up: 10 })
            .then(result => setRecData(result))
            .catch(e => console.error("Auto-generate failed", e))
            .finally(() => setGenerating(false));
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [orgId]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const result = await api.generateRecommendations();
      setRecData(result);
    } catch (e) {
      console.error(e);
      alert("Generation failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div data-testid="recommendations-page" className="p-6 sm:p-8 max-w-5xl mx-auto">
      <div className="mb-8 pb-6 border-b border-zinc-100 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-zinc-900 tracking-tight">Recommendations</h1>
          <p className="text-sm text-zinc-500 mt-1">Article ideas + internal linking opportunities</p>
        </div>
        <button
          onClick={handleGenerate}
          disabled={generating}
          data-testid="generate-recommendations-button"
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold tracking-wide transition-all rounded-lg ${
            generating ? "bg-zinc-100 text-zinc-400 cursor-not-allowed" : "text-white hover:opacity-90"
          }`}
          style={{ backgroundColor: generating ? "" : PRIMARY }}
        >
          <RefreshCw size={14} strokeWidth={2} className={generating ? "animate-spin" : ""} />
          {generating ? "GENERATING..." : recData.status === "not_generated" ? "GENERATE NOW" : "REGENERATE"}
        </button>
      </div>

      {/* From topic coverage — computed from search topics, no generation step */}
      <section className="mb-8">
        <div className="flex items-center gap-2 mb-1">
          <Lightbulb size={14} className="text-zinc-400" />
          <h2 className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-500">
            From your topic coverage {topicIdeas?.length > 0 ? `(${topicIdeas.length})` : ""}
          </h2>
        </div>
        <p className="text-xs text-zinc-400 mb-4">Search topics with demand but no page, or a page past page 1. Highest lead potential first.</p>
        {topicIdeas === null || loading ? (
          <div className="text-sm text-zinc-400 py-4">Loading...</div>
        ) : topicIdeas.length === 0 ? (
          <div className="bg-white rounded-xl border border-zinc-100 p-6 text-center shadow-sm">
            <p className="text-xs text-zinc-500">
              No uncovered search topics. Run Gap Analysis in Search Performance → Queries to build topics from your GSC queries.
            </p>
            <button
              onClick={() => navigate("/dashboard/search-performance?tab=queries")}
              className="mt-2 text-xs font-bold hover:underline"
              style={{ color: PRIMARY }}
            >
              Open Search Performance →
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {topicIdeas.map((item, i) => [
              <RecommendationCard
                key={item.topic_id}
                item={item}
                index={i}
                testPrefix="topic-idea"
                pushedOutline={pushedMap[item.title.trim().toLowerCase()]}
                pendingOutlineId={pendingMap[item.title.trim().toLowerCase()]}
                cms={cms}
                footerExtra={
                  <button
                    onClick={() => setOpenPlan(openPlan === item.topic_id ? null : item.topic_id)}
                    title="Split this topic's searches into a pillar page, cluster articles and the links between them"
                    className="ml-auto flex items-center gap-1 text-[10px] font-bold hover:underline"
                    style={{ color: PRIMARY }}
                  >
                    <Network size={11} />
                    {openPlan === item.topic_id ? "Hide plan" : "Plan cluster"}
                  </button>
                }
              />,
              openPlan === item.topic_id && (
                <div key={`plan-${item.topic_id}`} className="sm:col-span-2 bg-zinc-50 border border-zinc-100 rounded-xl p-4">
                  <TopicPlan idea={item} cms={cms} pushedMap={pushedMap} pendingMap={pendingMap} />
                </div>
              ),
            ])}
          </div>
        )}
      </section>

      {/* What to Write — AI ideas, banner linking to Writing page */}
      <section className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <Lightbulb size={14} className="text-zinc-400" />
          <h2 className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-500">
            More article ideas {recData.suggestions?.length > 0 ? `(${recData.suggestions.length})` : ""}
          </h2>
        </div>
        {loading ? (
          <div className="text-sm text-zinc-400 py-4">Loading...</div>
        ) : recData.status === "not_generated" || recData.suggestions?.length === 0 ? (
          <div className="bg-white rounded-xl border border-zinc-100 p-10 text-center shadow-sm">
            <Lightbulb size={36} color="#CCC" className="mx-auto" />
            <p className="text-sm font-bold text-zinc-900 mt-3">Want more angles?</p>
            <p className="text-xs text-zinc-500 mt-1 mb-4">
              AI turns your search topics{cms === false ? "" : " and article clusters"} into specific article titles beyond the topics above. Uses credits.
            </p>
            <button
              onClick={handleGenerate}
              disabled={generating}
              data-testid="generate-empty-state-button"
              className="px-5 py-2.5 text-white text-sm font-bold rounded-lg transition-colors hover:opacity-90"
              style={{ backgroundColor: PRIMARY }}
            >
              {generating ? "Generating..." : "Generate ideas"}
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-zinc-500">
                Generated {recData.generated_at ? new Date(recData.generated_at).toLocaleString() : ""} ·{" "}
                {recData.topic_count ? `${recData.topic_count} search topics · ` : ""}
                {recData.cluster_count} clusters · {recData.article_count} articles analyzed
              </p>
              <button
                onClick={() => navigate("/dashboard/writing")}
                className="flex items-center gap-1.5 text-xs font-bold transition-colors"
                style={{ color: PRIMARY }}
              >
                <NotebookPen size={12} />
                Write in editor →
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {recData.suggestions.map((item, i) => (
                <RecommendationCard
                  key={i}
                  item={item}
                  index={i}
                  pushedOutline={pushedMap[item.title?.trim().toLowerCase()]}
                  pendingOutlineId={pendingMap[item.title?.trim().toLowerCase()]}
                  cms={cms}
                />
              ))}
            </div>
          </>
        )}
      </section>

      {/* Internal Linking */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Link2 size={14} className="text-zinc-400" />
          <h2 className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-500">
            Internal Linking {linkData.suggestions?.length > 0 ? `(${linkData.suggestions.length})` : ""}
          </h2>
        </div>
        {loading ? (
          <div className="text-sm text-zinc-400 py-4">Loading...</div>
        ) : linkData.suggestions?.length === 0 ? (
          <div className="bg-white rounded-xl border border-zinc-100 p-10 text-center shadow-sm">
            <Link2 size={36} color="#CCC" className="mx-auto" />
            <p className="text-sm font-bold text-zinc-900 mt-3">No linking suggestions</p>
            <p className="text-xs text-zinc-500 mt-1">
              {cms === false
                ? "Article-to-article suggestions need a CMS. Without one, see the links to add in Knowledge Graph → Search demand."
                : "Sync your articles first to discover internal linking opportunities."}
            </p>
            {cms === false && (
              <button
                onClick={() => navigate("/dashboard/graph")}
                className="mt-3 text-xs font-bold hover:underline"
                style={{ color: PRIMARY }}
              >
                Open Knowledge Graph →
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="mb-3">
              <p className="text-xs text-zinc-500">
                {linkData.suggestions.length} article{linkData.suggestions.length !== 1 ? "s" : ""} with linking opportunities
              </p>
              <p className="text-xs text-zinc-400 mt-0.5">
                Links shown have 40–84% similarity (related but not cannibalizing)
              </p>
            </div>
            <div className="space-y-3">
              {linkData.suggestions.map((s, i) => (
                <LinkingCard key={i} suggestion={s} />
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
